import _ from 'lodash';
import {createSelector, ParametricSelector} from 'reselect';

import {onlyItem} from '../../../../libs/shared/helpers/src/utilities/arrayHelpers';
import {isTeamInbox, isTrackerSubscriber} from '../../../helpers/modelHelpers';
import {
  ConversationStatusesEnum,
  findConversationReminder,
  findConversationsCanceledReminder,
  findConversationsReminder,
  findConversationStatus,
  isConversationInTeamContext,
  isInternalConversation,
} from '../../../helpers/models/conversationHelpers';
import {AppState} from '../../appState';
import {CellsContext} from '../../panels/cells/context/cellsContext';
import {getConversations, getMessages} from '../dataSelectors';
import {getInboxes} from '../inboxes/inboxesSelectors';
import {getFindTeammate} from '../teammates/lazyTeammatesSelectors';
import {ConversationBasesSelector} from './conversationsSelectors';

type GetConversationIdsSelector = ParametricSelector<AppState, any, ReadonlyArray<number>>;
type GetMessageIdsSelector = ParametricSelector<AppState, any, ReadonlyArray<string>>;
type GetCellsContextSelector = ParametricSelector<AppState, any, CellsContext>;

export function makeGetConversationsFromIds(getConversationsIds: GetConversationIdsSelector) {
  return createSelector([getConversationsIds, getConversations], (conversationIds, conversations) =>
    _(conversationIds)
      .map((id) => conversations[id])
      .compact()
      .value(),
  );
}

export function makeGetConversationsFromMessageIds(getSelectedIds: GetMessageIdsSelector) {
  return createSelector(
    [getSelectedIds, getConversations, getMessages],
    (selectedIds, conversations, messages) => {
      if (!selectedIds) {
        return [];
      }

      return _(selectedIds)
        .uniq()
        .map((uid) => messages[uid])
        .compact()
        .map((m) => m.conversationId)
        .uniq()
        .map((id) => conversations[id])
        .compact()
        .value();
    },
  );
}

export function makeGetNamespacesFromConversations<P>(getCurrentConversations: ConversationBasesSelector<P>) {
  return createSelector([getCurrentConversations], (conversations) =>
    _(conversations)
      .map((c) => c.namespace)
      .uniq()
      .value(),
  );
}

/*
 * Assign.
 */

/** Find the assignee for the current conversations. */
export function makeGetAssigneeForAll<P>(getCurrentConversations: ConversationBasesSelector<P>) {
  return createSelector([getCurrentConversations, getFindTeammate], (conversations, findTeammate) => {
    // Find the assignee IDs for all the specified conversations.
    const assigneeIds = _(conversations)
      .map((c) => c.assigneeId)
      .uniq()
      .value();

    // If there is not exactly 1 assignee or the 1 assignee is undefined, there is no common assignee.
    const onlyAssigneeId = onlyItem(assigneeIds);
    if (!onlyAssigneeId) {
      return undefined;
    }

    // If one was found, return the teammate.
    return findTeammate(onlyAssigneeId, 'conversationsMultiSelectors makeGetAssigneeForAll');
  });
}

/*
 * Snooze.
 */

/** Find a common reminder for our user in all the conversations. */
export function makeGetReminderForAll<P>(
  getCellsContext: GetCellsContextSelector,
  getCurrentConversations: ConversationBasesSelector<P>,
) {
  return createSelector([getCurrentConversations, getCellsContext], (conversations, cellsContext) =>
    findConversationsReminder(cellsContext, conversations),
  );
}

/** Find common reminders for our user in all the conversations. */
export function makeGetRemindersForAll<P>(
  getCellsContext: GetCellsContextSelector,
  getCurrentConversations: ConversationBasesSelector<P>,
) {
  return createSelector([getCurrentConversations, getCellsContext], (conversations, cellsContext) =>
    _(conversations)
      .map((c) => findConversationReminder(cellsContext, c))
      .compact()
      .uniqBy((r) => r.remindAt)
      .sortBy((r) => r.isTeamReminder)
      .value(),
  );
}

/** Find a common canceled reminder for our user in all the conversations. */
export function makeGetCanceledReminderForAll<P>(
  getCellsContext: GetCellsContextSelector,
  getCurrentConversations: ConversationBasesSelector<P>,
) {
  return createSelector([getCurrentConversations, getCellsContext], (conversations, cellsContext) =>
    findConversationsCanceledReminder(cellsContext, conversations),
  );
}

/*
 * Archive.
 */

/** Check if all the conversations are closed/archived. */
export function makeGetAreAllArchived<P>(
  getCellsContext: GetCellsContextSelector,
  getCurrentConversations: ConversationBasesSelector<P>,
) {
  return createSelector(getCurrentConversations, getCellsContext, (conversations, cellsContext) =>
    conversations.every((c) => findConversationStatus(cellsContext, c) === ConversationStatusesEnum.ARCHIVED),
  );
}

/*
 * Trash.
 */

/** Check if all the conversations are trashed. */
export function makeGetAreAllTrashed<P>(
  getCellsContext: GetCellsContextSelector,
  getCurrentConversations: ConversationBasesSelector<P>,
) {
  return createSelector([getCurrentConversations, getCellsContext], (conversations, cellsContext) =>
    conversations.every((c) => findConversationStatus(cellsContext, c) === ConversationStatusesEnum.TRASHED),
  );
}

/*
 * Spam.
 */

/** Check if all the conversations are marked as spam. */
export function makeGetAreAllSpam<P>(
  getCellsContext: GetCellsContextSelector,
  getCurrentConversations: ConversationBasesSelector<P>,
) {
  return createSelector([getCurrentConversations, getCellsContext], (conversations, cellsContext) =>
    conversations.every((c) => findConversationStatus(cellsContext, c) === ConversationStatusesEnum.SPAM),
  );
}

/** Check if all the conversations are team conversations. */
export function makeGetAreAllShared<P>(getCurrentConversations: ConversationBasesSelector<P>) {
  return createSelector([getCurrentConversations, getInboxes], (conversations, inboxes) =>
    conversations.every((c) =>
      _(c.inboxIds)
        .map((i) => inboxes[i])
        .compact()
        .some(isTeamInbox),
    ),
  );
}

export function makeGetAreAllSubscribed<P>(
  getCellsContext: GetCellsContextSelector,
  getCurrentConversations: ConversationBasesSelector<P>,
) {
  return createSelector([getCurrentConversations, getCellsContext], (conversations, cellsContext) =>
    conversations.every((c) => isTrackerSubscriber(c.trackers[cellsContext.teammateId])),
  );
}

export function makeGetAreAllInternal<P>(getCurrentConversations: ConversationBasesSelector<P>) {
  return createSelector(getCurrentConversations, (conversations) =>
    conversations.every(isInternalConversation),
  );
}

export function makeGetSomeHaveOtherAssignee<P>(
  getCellsContext: GetCellsContextSelector,
  getCurrentConversations: ConversationBasesSelector<P>,
) {
  return createSelector([getCellsContext, getCurrentConversations], (cellsContext, conversations) =>
    conversations.some((c) =>
      Boolean(
        isConversationInTeamContext(cellsContext, c) &&
          c.assigneeId &&
          c.assigneeId !== cellsContext.teammateId,
      ),
    ),
  );
}
